import { useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import providersService from '../services/providers';
import metricsService from '../services/metrics';
import { rumService } from '../services/rum';
import { IMPRESSIONS } from '../constants/metrics';
import { AppConfig } from '../types/app';
import { IdProvider } from '../types/auth';
import { providerMessages } from '../i18n/provider.messages';
import messages from '../i18n/chooseSignInMethod.messages';
import styles from './ChooseSignInMethod.module.css';

interface ChooseSignInMethodProps {
  config: AppConfig;
  email: string;
  loginMethods: string[];
  onChangeEmail: () => void;
  onRedirectToIdp: (provider: IdProvider) => void;
}

const IDP_DESCRIPTION_KEYS: Record<string, keyof typeof messages> = {
  AWSBuilderID: 'awsBuilderIdDescription',
  LoginWithAmazon: 'loginWithAmazonDescription',
  AmazonFederate: 'amazonFederateDescription',
  AmazonSystemFederate: 'amazonFederateDescription',
  OTP: 'otpDescription',
};

function ChooseSignInMethod({
  config,
  email,
  loginMethods,
  onChangeEmail,
  onRedirectToIdp,
}: ChooseSignInMethodProps) {
  const intl = useIntl();
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>();
  const metricsPublisher = useRef(
    metricsService.getPublisher('ChooseSignInMethodPage')
  );

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    rumService.recordEvent('ChooseSignInMethodImpression', {
      loginMethods: loginMethods.join(','),
      url: window.location.href,
    });
  }, [loginMethods]);

  const handleContinue = () => {
    if (!selectedMethod) return;
    const provider = providersService.transformNameToIdProvider(
      selectedMethod,
      config.gandalfDomain
    );
    metricsPublisher.current.publishCounterMonitor('MethodSelected', 1);
    rumService.recordEvent('ChooseSignInMethodSelected', {
      provider: selectedMethod,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
    onRedirectToIdp(provider);
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.welcomeBack}>
        <FormattedMessage {...messages.welcomeBack} />
      </h1>
      <p className={styles.subtitle}>
        <span className={styles.subtitleLight}>
          <FormattedMessage {...messages.signingInWith} />
        </span>
        <span className={styles.subtitleBold}>{email}</span>
        <button
          type="button"
          className={styles.changeEmailLink}
          onClick={onChangeEmail}
          data-testid="choose-method-change-email"
        >
          <FormattedMessage {...messages.changeEmail} />
        </button>
      </p>

      <div className={styles.card}>
        <h2 className={styles.header}>
          <FormattedMessage {...messages.header} />
        </h2>

        <div className={styles.tiles}>
          {loginMethods.map((method) => (
            <div
              key={method}
              className={`${styles.tile} ${
                selectedMethod === method ? styles.tileSelected : ''
              }`}
              onClick={() => setSelectedMethod(method)}
              role="presentation"
            >
              <input
                type="radio"
                name="signInMethod"
                value={method}
                checked={selectedMethod === method}
                onChange={() => setSelectedMethod(method)}
                className={styles.radioInput}
                aria-label={
                  providerMessages[method]
                    ? intl.formatMessage(providerMessages[method])
                    : intl.formatMessage(messages.organizationalSsoLabel)
                }
              />
              <span className={styles.radioCircle} />
              <div className={styles.tileContent}>
                <span className={styles.tileLabel}>
                  {providerMessages[method]
                    ? intl.formatMessage(providerMessages[method])
                    : intl.formatMessage(messages.organizationalSsoLabel)}
                </span>
                <span className={styles.tileDescription}>
                  {IDP_DESCRIPTION_KEYS[method] ? (
                    <FormattedMessage
                      {...messages[IDP_DESCRIPTION_KEYS[method]]}
                    />
                  ) : (
                    intl.formatMessage(messages.organizationalSsoDescription)
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleContinue}
          disabled={!selectedMethod}
          data-testid="choose-method-continue"
        >
          <FormattedMessage {...messages.continueButton} />
        </button>
      </div>
    </div>
  );
}

export default ChooseSignInMethod;
